
import { dbManager } from './db_manager';
import { groqService } from './groqService';
import { bergamotModelService, loadBergamotModel } from './bergamotModelService';
import { isDictOfflineEnabled } from './dictionaryOfflineService';
import { marianModelService } from './marianModelService';
import { nllbModelService, checkNllbModelDownloaded } from './nllbModelService';
import i18n from '../i18n';

const CACHE_KEY = 'libritui_translation_cache';

export interface TranslationResult {
  translation: string;
  explanation: string;
  commentary?: string;
  detectedLanguage?: string;
  isoCode?: string;
  source: 'cache' | 'gemini' | 'google' | 'offline';
}

const translateWithGoogleFree = async (text: string, targetLanguage: string, sourceLanguage?: string): Promise<TranslationResult> => {
  const normalizeLang = (l?: string) => l ? l.trim().toLowerCase().normalize("NFD").replace(/[\u0300-\u036f]/g, "") : "";
  
  const langMap: Record<string, string> = {
    'portugues': 'pt', 'ingles': 'en', 'espanhol': 'es', 'frances': 'fr', 'alemao': 'de',
    'italiano': 'it', 'japones': 'ja', 'chines': 'zh', 'portugues do brasil': 'pt',
    'pt': 'pt', 'en': 'en', 'es': 'es', 'fr': 'fr', 'de': 'de', 'it': 'it', 'ja': 'ja', 'zh': 'zh', 'ru': 'ru', 'hu': 'hu'
  };

  let targetCode = langMap[normalizeLang(targetLanguage)] || targetLanguage;
  targetCode = targetCode?.split('-')[0] || 'pt';
  if (targetCode.length > 2) targetCode = 'pt';

  const sourceCode = sourceLanguage ? (langMap[normalizeLang(sourceLanguage)] || sourceLanguage).split('-')[0] : 'auto';

  // 1. Direct Client Google Translate API (clients5 - fastest, zero CORS, zero 500 error)
  try {
    const clients5Url = `https://clients5.google.com/translate_a/t?client=dict-chrome-ex&sl=${sourceCode}&tl=${targetCode}&q=${encodeURIComponent(text)}`;
    const response = await fetch(clients5Url);
    if (response.ok) {
      const data = await response.json();
      if (data && Array.isArray(data) && typeof data[0] === 'string' && data[0]) {
        return {
          translation: data[0],
          explanation: "Tradução rápida gratuita.",
          commentary: "Google API Direct",
          source: 'google'
        };
      }
    }
  } catch (err) {}

  // 1.5 Direct Client single endpoint
  try {
    const directUrl = `https://translate.googleapis.com/translate_a/single?client=gtx&sl=${sourceCode}&tl=${targetCode}&dt=t&q=${encodeURIComponent(text)}`;
    const response = await fetch(directUrl);
    if (response.ok) {
      const data = await response.json();
      if (data && Array.isArray(data) && Array.isArray(data[0])) {
        const translation = data[0]
          .filter((s: any) => s && typeof s[0] === 'string')
          .map((s: any) => s[0])
          .join('');
        if (translation) {
          return {
            translation,
            explanation: "Tradução rápida gratuita (direta).",
            commentary: "Google API Direct",
            detectedLanguage: data[2],
            isoCode: data[2],
            source: 'google'
          };
        }
      }
    }
  } catch (directErr) {}

  // 2. Try Proxy endpoint
  try {
    const url = `/api/translate?sl=${sourceCode}&tl=${targetCode}&q=${encodeURIComponent(text)}`;
    const response = await fetch(url);

    if (response.ok) {
      const data = await response.json();
      if (data && Array.isArray(data) && Array.isArray(data[0])) {
        const translation = data[0]
          .filter((s: any) => s && typeof s[0] === 'string')
          .map((s: any) => s[0])
          .join('');
        if (translation) {
          return {
            translation,
            explanation: "Tradução rápida gratuita.",
            commentary: "Google API Proxy",
            detectedLanguage: data[2],
            isoCode: data[2],
            source: 'google'
          };
        }
      }
    }
  } catch (err) {}

  // 3. Fallback to Local/R2 Dictionary Lookup IMMEDIATELY
  try {
    const dictRes = await tryTranslateOfflineR2(text, targetLanguage, sourceLanguage, true);
    if (dictRes && dictRes.translation) {
      return {
        translation: dictRes.translation,
        explanation: "Tradução via dicionário (Recuperação offline).",
        commentary: "R2 Dict Fallback",
        source: 'offline'
      };
    }
  } catch (dictErr) {}

  return {
    translation: text,
    explanation: "Tradução não encontrada",
    commentary: "Fallback",
    source: 'google'
  };
};

// --- Offline Dictionary R2 Cache & Lookup ---
const lang3Map: Record<string, string> = {
  en: 'eng', english: 'eng',
  pt: 'por', portuguese: 'por',
  it: 'ita', italian: 'ita',
  fr: 'fra', french: 'fra',
  es: 'spa', spanish: 'spa',
  de: 'deu', german: 'deu',
  zh: 'zho', chinese: 'zho',
  ja: 'jpn', japanese: 'jpn',
  ru: 'rus', russian: 'rus',
  hu: 'hun', hungarian: 'hun'
};

const r2DictMemoryCache = new Map<string, Record<string, string>>();

export const prefetchFreeDictCache = async (targetLang?: string, sourceLang?: string): Promise<void> => {
  if (typeof window === 'undefined' || !('caches' in window) || !isDictOfflineEnabled()) return;
  try {
    const nativeLang2 = (targetLang || (typeof localStorage !== 'undefined' ? (localStorage.getItem('i18nextLng') || localStorage.getItem('targetLanguage')) : 'pt') || 'pt').split('-')[0].toLowerCase();
    const sourceLang2 = (sourceLang || 'en').split('-')[0].toLowerCase();

    const pairsToFetch = [
      { src: sourceLang2, tgt: nativeLang2 },
      { src: 'en', tgt: nativeLang2 },
      { src: 'pt', tgt: nativeLang2 === 'pt' ? 'en' : nativeLang2 },
      { src: 'es', tgt: nativeLang2 },
      { src: 'de', tgt: nativeLang2 },
      { src: 'fr', tgt: nativeLang2 },
      { src: 'it', tgt: nativeLang2 }
    ];

    const cache = await caches.open('free-dict-cache');

    for (const p of pairsToFetch) {
      const src3 = lang3Map[p.src] || p.src;
      const tgt3 = lang3Map[p.tgt] || p.tgt;
      if (!src3 || !tgt3 || src3 === tgt3) continue;

      const pair = `${src3}-${tgt3}`;
      const r2Url = `https://pub-3d7ad7f5d04d46a5b7232c0baf46d866.r2.dev/offline-dict/${p.src}/${pair}/${pair}.json`;

      try {
        const existing = await cache.match(r2Url);
        if (!existing) {
          console.log(`[FreeDict] Background caching free-dict for ${pair}...`);
          const response = await fetch(r2Url);
          if (response.ok) {
            await cache.put(r2Url, response.clone());
            const dict = await response.clone().json();
            if (dict) r2DictMemoryCache.set(r2Url, dict);
            console.log(`[FreeDict] Pre-cached ${pair} successfully!`);
          }
        } else if (!r2DictMemoryCache.has(r2Url)) {
          const dict = await existing.json();
          if (dict) r2DictMemoryCache.set(r2Url, dict);
        }
      } catch (e) {}
    }
  } catch (err) {
    // Fail silently in background
  }
};

const tryTranslateOfflineR2 = async (
  text: string,
  targetLanguage: string,
  sourceLanguage?: string,
  ignoreOfflineFlag: boolean = false
): Promise<TranslationResult | null> => {
  try {
    const isOfflineEnabled = typeof localStorage !== 'undefined' && localStorage.getItem('libritui_offline_translator_enabled') === 'true';
    if (!isOfflineEnabled && !ignoreOfflineFlag) return null;

    const cleanWord = text.toLowerCase().replace(/[.,\/#!$%\^&\*;:{}=\-_`~()?\"']/g, "").trim();
    if (!cleanWord) return null;

    const nativeLang2 = (targetLanguage || (typeof localStorage !== 'undefined' ? localStorage.getItem('i18nextLng') : 'pt') || 'pt').split('-')[0].toLowerCase();
    const sourceLang2 = (sourceLanguage || 'en').split('-')[0].toLowerCase();

    const src3 = lang3Map[sourceLang2] || sourceLang2;
    const tgt3 = lang3Map[nativeLang2] || nativeLang2;
    const pair = `${src3}-${tgt3}`;
    const r2Url = `https://pub-3d7ad7f5d04d46a5b7232c0baf46d866.r2.dev/offline-dict/${sourceLang2}/${pair}/${pair}.json`;

    let dict: Record<string, string> | undefined = r2DictMemoryCache.get(r2Url);

    // Check CacheStorage first if not in memory
    if (!dict && typeof window !== 'undefined' && 'caches' in window && isDictOfflineEnabled()) {
      try {
        const cache = await caches.open('free-dict-cache');
        const cachedResponse = await cache.match(r2Url);
        if (cachedResponse && cachedResponse.ok) {
          dict = await cachedResponse.json();
          if (dict) r2DictMemoryCache.set(r2Url, dict);
        }
      } catch (cacheErr) {
        console.warn("[Translation] CacheStorage read failed:", cacheErr);
      }
    }

    if (!dict) {
      try {
        const response = await fetch(r2Url);
        if (response.ok) {
          dict = await response.clone().json();
          if (dict) {
            r2DictMemoryCache.set(r2Url, dict);
            if (typeof window !== 'undefined' && 'caches' in window && isDictOfflineEnabled()) {
              try {
                const cache = await caches.open('free-dict-cache');
                await cache.put(r2Url, response);
              } catch (e) {}
            }
          }
        }
      } catch (fetchErr) {
        console.warn("[Translation] R2 dict fetch failed:", fetchErr);
      }
    }

    if (dict) {
      const match = lookupWordInFreeDict(dict, cleanWord);
      if (match) {
        const primarySynonym = getPrimarySynonym(match);
        return {
          translation: primarySynonym || match,
          explanation: "Tradução via dicionário offline R2",
          commentary: "R2 Offline Dict",
          source: 'offline-dict'
        };
      }
    }

    // Try reverse lookup dictionary if direct dictionary didn't yield a result
    const reversePair = `${tgt3}-${src3}`;
    const reverseR2Url = `https://pub-3d7ad7f5d04d46a5b7232c0baf46d866.r2.dev/offline-dict/${nativeLang2}/${reversePair}/${reversePair}.json`;
    let dictReverse: Record<string, string> | undefined = r2DictMemoryCache.get(reverseR2Url);
    if (!dictReverse && typeof window !== 'undefined' && 'caches' in window && isDictOfflineEnabled()) {
      try {
        const cache = await caches.open('free-dict-cache');
        const cachedResp = await cache.match(reverseR2Url);
        if (cachedResp && cachedResp.ok) {
          dictReverse = await cachedResp.json();
          if (dictReverse) r2DictMemoryCache.set(reverseR2Url, dictReverse);
        }
      } catch (e) {}
    }

    if (!dictReverse) {
      try {
        const resp = await fetch(reverseR2Url);
        if (resp.ok) {
          dictReverse = await resp.clone().json();
          if (dictReverse) {
            r2DictMemoryCache.set(reverseR2Url, dictReverse);
            if (typeof window !== 'undefined' && 'caches' in window && isDictOfflineEnabled()) {
              try {
                const cache = await caches.open('free-dict-cache');
                await cache.put(reverseR2Url, resp);
              } catch (e) {}
            }
          }
        }
      } catch (e) {}
    }

    if (dictReverse) {
      const cleanWordLower = cleanWord.toLowerCase();
      for (const [key, val] of Object.entries(dictReverse)) {
        const valLower = val.toLowerCase();
        const parts = valLower.split(/[,;\/]/).map(p => p.trim());
        if (parts.includes(cleanWordLower) || valLower === cleanWordLower) {
          const primarySynonym = getPrimarySynonym(key);
          return {
            translation: primarySynonym || key,
            explanation: "Tradução via dicionário offline R2 (reverso)",
            commentary: "R2 Offline Dict Reverse",
            source: 'offline-dict'
          };
        }
      }
    }
  } catch (err) {
    console.warn("[Translation] Offline R2 lookup failed:", err);
  }
  return null;
};

const lookupWordInFreeDict = (dict: Record<string, string>, word: string): string | null => {
  const w = word.toLowerCase().trim();
  if (!w) return null;
  if (dict[w]) return dict[w];
  if (dict[word]) return dict[word];

  // Case-insensitive key lookup strictly preserving accents & diacritics (e.g. 'bär' != 'bar')
  for (const key of Object.keys(dict)) {
    if (key.toLowerCase() === w) {
      return dict[key];
    }
  }

  // Common inflections & stemming fallbacks (preserving accents)
  if (w.endsWith('ies')) {
    const s1 = w.slice(0, -3) + 'y';
    if (dict[s1]) return dict[s1];
    const s2 = w.slice(0, -3) + 'ie';
    if (dict[s2]) return dict[s2];
    const s3 = w.slice(0, -2);
    if (dict[s3]) return dict[s3];
  }
  if (w.endsWith('es')) {
    const s1 = w.slice(0, -2);
    if (dict[s1]) return dict[s1];
    const s2 = w.slice(0, -1);
    if (dict[s2]) return dict[s2];
  }
  if (w.endsWith('s')) {
    const s1 = w.slice(0, -1);
    if (dict[s1]) return dict[s1];
  }
  if (w.endsWith('ed')) {
    const s1 = w.slice(0, -2);
    if (dict[s1]) return dict[s1];
    const s2 = w.slice(0, -1);
    if (dict[s2]) return dict[s2];
  }
  if (w.endsWith('ing')) {
    const s1 = w.slice(0, -3);
    if (dict[s1]) return dict[s1];
    const s2 = w.slice(0, -3) + 'e';
    if (dict[s2]) return dict[s2];
  }
  return null;
};

const getPrimarySynonym = (text: string): string => {
  if (!text) return '';
  const first = text.split(/[,;\/]/)[0].trim();
  return first;
};

const applyFreeDictWordFallback = async (
  translatedText: string,
  originalText: string,
  targetLanguage: string,
  sourceLanguage?: string
): Promise<string> => {
  let result = translatedText;
  try {
    const sourceLang2 = (sourceLanguage || 'en').split('-')[0].toLowerCase();
    const nativeLang2 = (targetLanguage || 'pt').split('-')[0].toLowerCase();

    const src3 = lang3Map[sourceLang2] || sourceLang2;
    const tgt3 = lang3Map[nativeLang2] || nativeLang2;
    const pair = `${src3}-${tgt3}`;
    const r2Url = `https://pub-3d7ad7f5d04d46a5b7232c0baf46d866.r2.dev/offline-dict/${sourceLang2}/${pair}/${pair}.json`;

    let dict: Record<string, string> | undefined = r2DictMemoryCache.get(r2Url);
    if (!dict) {
      const response = await fetch(r2Url);
      if (response.ok) {
        dict = await response.json();
        if (dict) r2DictMemoryCache.set(r2Url, dict);
      }
    }

    if (dict) {
      const originalWords = originalText.split(/[\s']+/);
      for (const rawWord of originalWords) {
        const cleanWord = rawWord.toLowerCase().replace(/[.,\/#!$%\^&\*;:{}=\-_`~()?\"']/g, "").trim();
        if (cleanWord.length > 1) {
          const dictMatch = lookupWordInFreeDict(dict, cleanWord);
          if (dictMatch) {
            const primarySynonym = getPrimarySynonym(dictMatch);
            if (primarySynonym) {
              const regex = new RegExp(`\\b${cleanWord}\\b`, 'gi');
              if (regex.test(result)) {
                result = result.replace(regex, primarySynonym);
              }
            }
          }
        }
      }
    }
  } catch (err) {
    console.warn("[TranslationService] FreeDict word fallback error:", err);
  }
  return result;
};

export interface UnifiedTranslateOptions {
  isGrammar?: boolean;
  isSingleWord?: boolean;
}

export const translationService = {
  async unifiedTranslate(
    text: string, 
    targetLanguage: string, 
    userId?: string, 
    sourceLanguage?: string, 
    bookId?: string,
    isPro: boolean = false,
    isSmartEnabled: boolean = true,
    options?: UnifiedTranslateOptions
  ): Promise<TranslationResult> {
    const cleanText = text.trim();
    if (!cleanText) {
      return { translation: '', source: 'none' };
    }

    const words = cleanText.split(/\s+/).filter(Boolean);
    const isSingleWord = options?.isSingleWord !== undefined ? options.isSingleWord : (words.length === 1);
    const isGrammar = options?.isGrammar || false;

    // 1. Check Level 1: Rich Dictionary (R2 Cloudflare - Novo) for single words
    if (isSingleWord && !isGrammar) {
      try {
        const { lookupRichDictionary } = await import('./richDictionaryService');
        const richItem = await lookupRichDictionary(cleanText, sourceLanguage || 'en', targetLanguage);
        if (richItem && richItem.primary_translation) {
          return {
            translation: richItem.primary_translation,
            explanation: "Dicionário Rico (R2 Cloudflare)",
            commentary: "R2 Rich Dict",
            source: 'offline'
          };
        }
      } catch (richErr) {
        console.warn("[TranslationService] Rich Dictionary Level 1 lookup error:", richErr);
      }
    }

    // 1.5. Check Offline Mode First
    const isOfflineEnabled = typeof localStorage !== 'undefined' && localStorage.getItem('libritui_offline_translator_enabled') === 'true';
    if (isOfflineEnabled) {
      console.log(`[TRADUTOR] Modo offline ativado. (${sourceLanguage || 'auto'} -> ${targetLanguage}) | isSingleWord: ${isSingleWord} | isGrammar: ${isGrammar}`);

      // RULE A: Single Word Clicks in Exercise Pages -> Try FreeDict Direct Lookup First
      if (isSingleWord && !isGrammar) {
        console.log(`[TRADUTOR] Palavra única detectada. Consultando FreeDict direto...`);
        const dictRes = await tryTranslateOfflineR2(cleanText, targetLanguage, sourceLanguage, true);
        if (dictRes && dictRes.translation) {
          const primarySynonym = getPrimarySynonym(dictRes.translation);
          return {
            translation: primarySynonym || dictRes.translation,
            explanation: "Tradução via dicionário offline R2",
            commentary: "R2 Offline Dict",
            source: 'offline-dict'
          };
        }
        console.log(`[TRADUTOR] Palavra única não encontrada no FreeDict. Executando modelo neural NLLB ONNX...`);
      }

      // RULE B: Grammar Explanation Paragraphs OR Phrases/Sentences OR Single-Word Plan B -> Use NLLB ONNX Multilingual Model
      let nllbError: any = null;
      try {
        const nllbRes = await nllbModelService.translateOffline(cleanText, targetLanguage, sourceLanguage || 'en');
        if (nllbRes && nllbRes.translation) {
          const isEcho = nllbRes.translation.toLowerCase().trim() === cleanText.toLowerCase().trim();
          if (!isEcho) {
            return {
              translation: nllbRes.translation,
              explanation: "Tradução via modelo neural NLLB ONNX (offline)",
              commentary: "NLLB ONNX Offline",
              source: 'offline-nllb'
            };
          } else {
            nllbError = new Error(`[NLLB ONNX Error]: O motor NLLB ONNX retornou um eco (texto não traduzido) para "${cleanText}".`);
          }
        } else {
          nllbError = new Error(`[NLLB ONNX Error]: O motor NLLB ONNX offline retornou resposta nula ao traduzir "${cleanText}".`);
        }
      } catch (e) {
        nllbError = e;
      }

      console.error("[TRADUTOR OFFLINE] Erro do NLLB ONNX:", nllbError);
      
      const friendlyOfflineError = i18n.t('translation.offlineError', 'Tradução indisponível, tente novamente mais tarde.');
      return {
        translation: friendlyOfflineError,
        explanation: friendlyOfflineError,
        commentary: "Erro NLLB ONNX",
        source: 'offline-nllb'
      };
    }

    const isBrowserOffline = typeof navigator !== 'undefined' && !navigator.onLine;
    if (isSingleWord && isBrowserOffline) {
      try {
        const dictRes = await tryTranslateOfflineR2(cleanText, targetLanguage, sourceLanguage, true);
        if (dictRes && dictRes.translation) {
          const primarySynonym = getPrimarySynonym(dictRes.translation);
          return {
            translation: primarySynonym || dictRes.translation,
            explanation: "Tradução via dicionário offline R2 (FreeDict)",
            commentary: "R2 Offline Dict",
            source: 'offline-dict'
          };
        }
      } catch (dictErr) {}
    }

    try {
      let result: TranslationResult;
      
      if (isPro && isSmartEnabled) {
        console.log(`[Translation] Chamando Groq AI (Literary)... Target: ${targetLanguage}`);
        try {
          const groqResult = await groqService.literaryTranslate(cleanText, targetLanguage, targetLanguage);
          result = {
            translation: groqResult.translation,
            explanation: groqResult.explanation,
            commentary: "Motor Groq (Smart Translation)",
            source: 'gemini',
            detectedLanguage: sourceLanguage
          };
        } catch (groqErr) {
          console.warn("[Translation] Groq AI call failed, falling back to Google/Offline:", groqErr);
          result = await translateWithGoogleFree(cleanText, targetLanguage, sourceLanguage);
        }
      } else {
        result = await translateWithGoogleFree(cleanText, targetLanguage, sourceLanguage);
      }

      return result;
    } catch (err: any) {
      // Fallback: try offline dictionary/models before throwing offline error
      try {
        const dictRes = await tryTranslateOfflineR2(cleanText, targetLanguage, sourceLanguage, true);
        if (dictRes && dictRes.translation) {
          return dictRes;
        }
      } catch (dictErr) {}

      try {
        const nllbRes = await nllbModelService.translateOffline(cleanText, targetLanguage, sourceLanguage || 'en');
        if (nllbRes && nllbRes.translation && nllbRes.translation.toLowerCase().trim() !== cleanText.toLowerCase().trim()) {
          return {
            translation: nllbRes.translation,
            explanation: "Tradução via modelo neural NLLB ONNX (offline)",
            commentary: "NLLB ONNX Offline",
            source: 'offline-nllb'
          };
        }
      } catch (nllbErr) {}

      const isBrowserOffline = typeof navigator !== 'undefined' && !navigator.onLine;
      if (isBrowserOffline) {
        const friendlyOnlineUnavailable = i18n.t('translation.onlineUnavailable', 'Tradução online indisponível no momento. Vá até configurações e habilite a tradução offline para continuar utilizando essa funcionalidade.');
        return {
          translation: friendlyOnlineUnavailable,
          explanation: friendlyOnlineUnavailable,
          commentary: "Falha na resposta (Offline)",
          source: 'google'
        };
      } else {
        const genericError = "Tradução indisponível no momento";
        return {
          translation: genericError,
          explanation: genericError,
          commentary: "Falha na resposta",
          source: 'google'
        };
      }
    }
  },

  async getBookTranslations(userId: string, bookId: string) {
    return dbManager.from('translations_cache', userId).select({ book_id: bookId ? String(bookId) : '' });
  },

  async getUserNotes(userId: string, bookId: string) {
    return dbManager.from('translations_cache', userId).select({ book_id: String(bookId), type: 'note' });
  },

  async saveUserNote(userId: string, data: {
    id: string;
    book_id: string;
    book_title: string;
    original: string;
    translation: string;
    page_number: number;
    favorited?: 'yes' | 'no';
  }) {
    // Map to translations_cache columns
    return dbManager.from('translations_cache', userId).upsert({
      id: data.id,
      user_id: userId,
      book_id: String(data.book_id),
      text: data.original,
      translation: data.translation,
      // Mapping to legacy columns for D1 visibility
      original_text: data.original,
      translated_text: data.translation,
      cache_key: data.id, // Using Note ID as cache key for easy lookup
      page_number: data.page_number,
      favorited: data.favorited || 'no',
      type: 'note',
      created_at: new Date().toISOString()
    });
  },

  async deleteUserNote(userId: string, id: string) {
    return dbManager.from('translations_cache', userId).delete(id);
  },

  async saveBookTranslation(userId: string, data: {
    id: string;
    book_id: string;
    page_number: number;
    text: string;
    translation: string;
    type: string;
  }) {
    return dbManager.from('translations_cache', userId).upsert({
      user_id: userId,
      ...data,
      original_text: data.text,
      translated_text: data.translation,
      book_id: String(data.book_id),
      created_at: new Date().toISOString()
    });
  },

  async deleteTranslation(userId: string, id: string) {
    return dbManager.from('translations_cache', userId).delete(id);
  },

  async translateWordWithFallback(
    text: string,
    targetLanguage: string,
    sourceLanguage?: string
  ): Promise<TranslationResult> {
    const cleanText = text.trim();
    if (!cleanText) {
      return { translation: '', explanation: '', source: 'offline' };
    }

    // 1. Try Rich Dictionary (R2 Cloudflare - Novo) first
    try {
      const { lookupRichDictionary } = await import('./richDictionaryService');
      const richItem = await lookupRichDictionary(cleanText, sourceLanguage || 'en', targetLanguage);
      if (richItem && richItem.primary_translation) {
        return {
          translation: richItem.primary_translation,
          explanation: "Dicionário Rico (R2 Cloudflare)",
          commentary: "R2 Rich Dict",
          source: 'offline'
        };
      }
    } catch (err) {
      console.warn("Rich Dictionary lookup failed:", err);
    }

    // 2. Try FreeDict (offline R2 dict fetch)
    try {
      const dictRes = await tryTranslateOfflineR2(cleanText, targetLanguage, sourceLanguage, true);
      if (dictRes && dictRes.translation && dictRes.translation.toLowerCase().trim() !== cleanText.toLowerCase().trim()) {
        return {
          translation: dictRes.translation,
          explanation: "Tradução via dicionário FreeDict",
          commentary: "FreeDict",
          source: 'offline'
        };
      }
    } catch (err) {
      console.warn("FreeDict translation failed:", err);
    }

    // 2. Try Bergamot model
    try {
      const bergamotRes = await bergamotModelService.translateOffline(cleanText, targetLanguage, sourceLanguage || 'en');
      if (bergamotRes && bergamotRes.translation && bergamotRes.translation.toLowerCase().trim() !== cleanText.toLowerCase().trim()) {
        return {
          translation: bergamotRes.translation,
          explanation: "Tradução via modelo Bergamot",
          commentary: "Bergamot Wasm",
          source: 'offline'
        };
      }
    } catch (err) {
      console.warn("Bergamot translation failed:", err);
    }

    // 3. Fallback to Google Translate free
    try {
      return await translateWithGoogleFree(cleanText, targetLanguage, sourceLanguage);
    } catch (err) {
      console.error("Google Translate free failed:", err);
      return {
        translation: "Tradução indisponível",
        explanation: "Todos os serviços de tradução falharam.",
        commentary: "Falha geral",
        source: 'google'
      };
    }
  },

  async syncTranslationsCacheFromD1(): Promise<void> {
    // Deprecated: Migrated to 100% Client-Side IndexedDB
    // Keeping method signature so any components calling it don't break.
    return Promise.resolve();
  }
};

/**
 * Textos longos (ex.: sinopse de audiobook): Google gratuito primeiro, em blocos;
 * se o Google falhar, usa o modelo local NLLB, mas só quando ele JÁ está instalado (nunca dispara download).
 */
export const translateLongTextGoogleFirst = async (
  text: string,
  targetLanguage: string,
  sourceLanguage?: string,
): Promise<{ translation: string; source: 'google' | 'offline-nllb' } | null> => {
  const clean = text.replace(/\s+\n/g, '\n').trim();
  if (!clean) return null;

  // blocos de até ~1500 caracteres, respeitando parágrafos e frases (limite de URL do Google)
  const blocks: string[] = [];
  for (const para of clean.split(/\n{1,}/)) {
    let rest = para.trim();
    while (rest.length > 1500) {
      const cut = Math.max(rest.lastIndexOf('. ', 1500), rest.lastIndexOf(' ', 1500), 1000);
      blocks.push(rest.slice(0, cut + 1).trim());
      rest = rest.slice(cut + 1).trim();
    }
    if (rest) blocks.push(rest);
  }

  try {
    const out: string[] = [];
    for (const b of blocks) {
      const r = await translateWithGoogleFree(b, targetLanguage, sourceLanguage);
      const ok = r && r.source === 'google' && r.translation && r.translation.trim() !== b.trim();
      if (!ok) throw new Error('google-failed');
      out.push(r.translation);
    }
    return { translation: out.join('\n\n'), source: 'google' };
  } catch {
    // segue para o modelo local
  }

  try {
    if (await checkNllbModelDownloaded()) {
      const out: string[] = [];
      for (const b of blocks) {
        const r = await nllbModelService.translateOffline(b, targetLanguage, sourceLanguage || 'en');
        if (!r?.translation) throw new Error('nllb-failed');
        out.push(r.translation);
      }
      return { translation: out.join('\n\n'), source: 'offline-nllb' };
    }
  } catch {
    // sem tradução disponível
  }
  return null;
};
